import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { ICorrectionSubmission, IDeliveryChallanLot, IExcessItemsEntry } from '@admin-panel/types';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import dayjs from 'dayjs';
import { axiosInstance } from './axios';
import CrudAPIClient from './crudapiclient';

type SyncFromUnicomMethod = (
  sorters: CrudSort[],
  filters: CrudFilters,
  payload: any,
  query: any,
  headers: any,
  meta: MetaQuery | undefined,
) => Promise<any>;

interface DeliveryChallanRefineCustomMethods {
  syncFromUnicom: SyncFromUnicomMethod;
  transferFacility: SyncFromUnicomMethod;
}

class DeliveryChallanAPIClient extends CrudAPIClient implements DeliveryChallanRefineCustomMethods {
  constructor() {
    super(GATEWAY_API_URL + '/deliverychallan');
  }

  transformNode(node: any) {
    node.summary = {
      totalItems: node.totalPendingCount || 0,
      pendingScan: node.totalPendingCount || 0,
      qcPassed: 0,
      qcFailed: 0,
      qcRejected: 0,
    };

    if (node.lots && Object.keys(node.lots).length > 0) {
      const lotsFinalMap: { [lotId: number]: IDeliveryChallanLot } = {};

      Object.keys(node.lots).forEach((lotIdStr: string) => {
        const lotId: number = parseInt(lotIdStr);
        const lot: any = node.lots[lotId];
        const lotScanSummary: any = {
          totalItems: 0,
          qcPassed: 0,
          qcFailed: 0,
          qcRejected: 0,
        };

        if (lot.statusCounts && lot.statusCounts.length > 0) {
          lot.statusCounts.forEach((row: any) => {
            switch (row.status) {
              case 'PASSED':
                lotScanSummary.qcPassed += row.count;
                node.summary.qcPassed += row.count;
                break;
              case 'REJECTED':
                lotScanSummary.qcRejected += row.count;
                node.summary.qcRejected += row.count;
                break;
              case 'FAILED':
                lotScanSummary.qcFailed += row.count;
                node.summary.qcFailed += row.count;
                break;
            }
            lotScanSummary.totalItems += row.count;
            node.summary.totalItems += row.count;
          });
        }
        if (lot.id == node.currentLotId) {
          lotScanSummary.pendingScan = node.totalPendingCount || 0;
          lotScanSummary.totalItems += lotScanSummary.pendingScan;
        }
        lot.scanSummary = lotScanSummary;
        lotsFinalMap[lotId] = lot;
      });
      if (!lotsFinalMap[node.currentLotId] && node.totalPendingCount > 0) {
        lotsFinalMap[node.currentLotId] = {
          id: node.currentLotId,
          scanSummary: {
            totalItems: 0,
            pendingScan: node.totalPendingCount || 0,
            qcPassed: 0,
            qcFailed: 0,
            qcRejected: 0,
          },
        };
      }

      node.lots = lotsFinalMap;
    }
    if (node.arrivedOn) {
      node.arrivedOn = dayjs(node.arrivedOn);
    }
    if (node.documentDate) {
      node.documentDate = dayjs(node.documentDate);
    }
    if (node.expectedDeliveryDate) {
      node.expectedDeliveryDate = dayjs(node.expectedDeliveryDate);
    }

    return node;
  }

  async syncFromUnicom(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/sync-from-unicom`;
    const response = await axiosInstance.post(url);
    return this.transformNode(response.data?.data);
  }

  async transferFacility(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/transfer-facility/${meta?.facilityId}`;
    const response = await axiosInstance.post(url);
    return this.transformNode(response.data?.data);
  }

  async previewCorrections(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/corrections/preview`;
    const response = await axiosInstance.post(url);
    return response.data?.data ?? [];
  }

  async correctionHistory(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/corrections/history`;
    const response = await axiosInstance.get(url);
    return response.data?.data ?? [];
  }

  async submitCorrections(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: ICorrectionSubmission,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/corrections/submit`;
    const response = await axiosInstance.post(url, payload);
    return this.transformNode(response.data?.data);
  }

  async updateExcess(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: IExcessItemsEntry,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/excess`;
    const response = await axiosInstance.post(url, payload);
    return this.transformNode(response.data?.data);
  }

  async clearItemCorrection(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/items/${meta?.itemId}/clear-correction`;
    const response = await axiosInstance.post(url);
    return response.data?.data;
  }

  async grnDone(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: { markMissingItemIds?: number[] },
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/grn-done`;
    const response = await axiosInstance.post(url, payload ?? {});
    return this.transformNode(response.data?.data);
  }

  async resetItems(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const url = `${this.baseURL}/${meta?.deliveryChallanId}/items/reset`;
    const response = await axiosInstance.post(url);
    return this.transformNode(response.data?.data);
  }

  async search(query: any, page: number, size: number, meta: MetaQuery | undefined): Promise<any> {
    try {
      /*
      const vendorIdFilter = query.filters.find(
        (f: SearchFilter) => f.field === 'vendorId'
      );
      if (!vendorIdFilter) {
        return [];
      }*/
      return await super.search(query, page, size, meta);
    } catch (error) {
      console.error(`Error searching entities with Query:`, query, error);
      throw error;
    }
  }
}

export default DeliveryChallanAPIClient;
